import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  Bot,
  Calendar,
  Code2,
  FileCode,
  MessageSquareText,
  Settings as SettingsIcon,
  ShieldCheck,
} from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import TrackingScriptSection from './TrackingScriptSection';
import VerificationSection from './VerificationSection';
import BotAccessSection from './BotAccessSection';
import LlmsTxtSection from './LlmsTxtSection';
import AutoPilotSection from './AutoPilotSection';
import BrandVoiceSection from './BrandVoiceSection';

// Section list — order = display order in the rail. Each section receives
// the currently-selected domain as a prop.
const SECTIONS = [
  { to: 'tracking', label: 'Tracking script', icon: Code2, Component: TrackingScriptSection },
  { to: 'verification', label: 'Verification', icon: ShieldCheck, Component: VerificationSection },
  { to: 'bots', label: 'AI bot access', icon: Bot, Component: BotAccessSection },
  { to: 'llms-txt', label: 'llms.txt', icon: FileCode, Component: LlmsTxtSection },
  { to: 'brand-voice', label: 'Brand voice', icon: MessageSquareText, Component: BrandVoiceSection },
  { to: 'autopilot', label: 'AutoPilot schedule', icon: Calendar, Component: AutoPilotSection },
];

// Left rail on md+, top dropdown on smaller screens. Mirrors the
// AccountLayout pattern so the two settings pages feel identical.
export default function SettingsLayout({ domain }) {
  return (
    <div className="space-y-5">
      <PageHeader
        variant="gradient"
        icon={SettingsIcon}
        eyebrow="Domain settings"
        title={`Domain settings · ${domain.domain}`}
        description="Per-domain configuration: tracking script, ownership verification, AI bot access, llms.txt, and AutoPilot schedule."
        help={{
          title: 'Domain vs. Account',
          body: 'These settings apply only to the currently selected domain. Account-wide settings (password, AI key, integrations) live under Account.',
        }}
      />

      <div className="md:hidden">
        <MobileSectionPicker />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5 items-start">
        <SectionRail />
        <div className="min-w-0">
          <Routes>
            <Route index element={<Navigate to="tracking" replace />} />
            {SECTIONS.map(({ to, Component }) => (
              <Route key={to} path={to} element={<Component domain={domain} />} />
            ))}
            <Route path="*" element={<Navigate to="tracking" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function SectionRail() {
  return (
    <aside className="hidden md:block sticky top-4">
      <nav className="card !p-2">
        <ul className="space-y-0.5">
          {SECTIONS.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-brand-50 text-brand-700 font-medium'
                      : 'text-ink-600 hover:bg-gray-50'
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

function MobileSectionPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const current =
    SECTIONS.find((s) => location.pathname.endsWith(`/settings/${s.to}`))?.to || 'tracking';
  return (
    <div className="card !p-2">
      <select
        className="input"
        onChange={(e) => navigate(`/settings/${e.target.value}`)}
        value={current}
      >
        {SECTIONS.map(({ to, label }) => (
          <option key={to} value={to}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}
